/**
 * O modelo de DOMÍNIO do front para o documento exigido pelo passo (`TP-057`). Não é o contrato da API: o fio é
 * `@anery/api-contracts`, e a tradução (texto anulável → string, data → `Date`) mora em `api.ts`, e só lá.
 *
 * Os tokens (`pendente`, `recebido`, `confere`...) são os que o banco persiste e a API entrega, em pt-BR. O que muda aqui
 * é o ROTULO: o token nunca aparece cru para quem opera.
 */

/** Cinco estados, o vocabulário da Regulação. `recebido` NÃO é `validado`: a tela nunca os mostra com o mesmo rótulo. */
export type EstadoDoDocumento = 'pendente' | 'recebido' | 'validado' | 'dispensado' | 'nao_aplicavel';

export type VereditoDaLeitura = 'confere' | 'diverge' | 'inconclusivo' | 'sem_texto';

export type EstadoDaLeitura = 'pendente' | 'concluida' | 'falhou';

export type AcaoNoDocumento = 'conferir' | 'dispensar' | 'reabrir';

export interface ExigenciaVista {
  readonly chave: string;
  readonly descricao: string;
  /** `null`: a leitura não viu (ou ainda não leu). Só uma pessoa valida. */
  readonly atendida: boolean | null;
}

export interface DocumentoExigido {
  readonly id: string;
  readonly frente: string;
  readonly passo: number;
  readonly passoRotulo: string;
  readonly item: string;
  readonly rotulo: string;
  readonly obrigatorio: boolean;
  readonly estado: EstadoDoDocumento;
  /** O item está fechado (regra D7, calculada no servidor): a tela só mostra. */
  readonly fechado: boolean;
  readonly exigeConferencia: boolean;
  readonly veredito: VereditoDaLeitura | null;
  /** Código do motivo da leitura (`documento_de_outra_pessoa`...), nunca texto livre. */
  readonly motivo: string | null;
  readonly motivoDaPessoa: string | null;
  readonly documentoId: string | null;
  readonly nomeDoDocumento: string | null;
  readonly exigencias: readonly ExigenciaVista[];
}

/** Um dado lido do documento: a regra mapeia, a IA confirma, corrige e acrescenta (vale a IA). */
export interface MetadadoLido {
  readonly chave: string;
  readonly rotulo: string;
  readonly valor: string;
  readonly origem: 'regra' | 'agente';
}

export interface DocumentoDoCaso {
  readonly id: string;
  readonly origem: 'email' | 'iw' | 'upload';
  readonly nomeArquivo: string;
  readonly leitura: EstadoDaLeitura;
  readonly registradoEm: Date;
  readonly ultimoVeredito: VereditoDaLeitura | null;
  readonly ultimoMotivo: string | null;
  readonly itemPretendidoId: string | null;
  readonly ultimoItemId: string | null;
  /** Descartado como "não serve para este caso": sai da lista de soltos, nada se apaga e volta com um clique. */
  readonly descartadoEm: Date | null;
  readonly metadados: readonly MetadadoLido[];
  /** O tipo que o agente 1 leu (`formulario_avaliacao`, `nead`...), ou `null` quando não reconheceu nenhum. */
  readonly tipoLido: string | null;
}

/** O campo do cadastro do paciente que um dado de documento validado alimenta. */
export type CampoDoCadastro =
  | 'nome'
  | 'cpf'
  | 'carteira'
  | 'data_nascimento'
  | 'nome_da_mae'
  | 'endereco'
  | 'telefone'
  | 'responsavel'
  | 'hospital';

export interface FonteDoDado {
  readonly documentoId: string;
  readonly arquivo: string;
  /** O item que o documento sustenta ("Formulário de avaliação"). */
  readonly item: string;
}

export interface ValorDoDado {
  readonly valor: string;
  readonly fontes: readonly FonteDoDado[];
}

/**
 * Um dado do caso que veio de documento VALIDADO por uma pessoa. `diverge`: dois documentos validados dizem coisas
 * diferentes, e `valores` traz os dois; a tela não escolhe.
 */
export interface DadoDoCaso {
  readonly chave: string;
  readonly rotulo: string;
  readonly cadastro: CampoDoCadastro | null;
  readonly diverge: boolean;
  readonly valores: readonly ValorDoDado[];
}

export interface DocumentosDoCaso {
  readonly exigidos: readonly DocumentoExigido[];
  readonly documentos: readonly DocumentoDoCaso[];
  readonly dados: readonly DadoDoCaso[];
}

export const ROTULO_DO_CADASTRO: Record<CampoDoCadastro, string> = {
  nome: 'Nome do paciente',
  cpf: 'CPF',
  carteira: 'Carteira',
  data_nascimento: 'Data de nascimento',
  nome_da_mae: 'Nome da mãe',
  endereco: 'Endereço',
  telefone: 'Telefone',
  responsavel: 'Responsável',
  hospital: 'Hospital',
};

/** O tipo que o agente 1 leu, na língua da operação. Token fora da lista aparece como veio. */
export const ROTULO_DO_TIPO_LIDO: Record<string, string> = {
  formulario_avaliacao: 'Formulário de avaliação',
  nead: 'NEAD (tabela de avaliação)',
  processo_inicial: 'Processo inicial',
  termo_solicitacao_id: 'Termo de solicitação de internação domiciliar',
  programa_id: 'Programa de internação domiciliar',
  orcamento: 'Orçamento',
  comunicado_implantacao: 'Comunicado de implantação',
};

export const ROTULO_DO_ESTADO: Record<EstadoDoDocumento, string> = {
  pendente: 'Pendente',
  recebido: 'Recebido',
  validado: 'Validado',
  dispensado: 'Dispensado',
  nao_aplicavel: 'Não se aplica',
};

export const ROTULO_DA_ORIGEM: Record<DocumentoDoCaso['origem'], string> = {
  email: 'e-mail',
  iw: 'IW',
  upload: 'enviado aqui',
};

/** O que a leitura disse, na língua da operação. `sem_texto` diz o que fazer: validar à mão. */
export const ROTULO_DO_VEREDITO: Record<VereditoDaLeitura, string> = {
  confere: 'Leitura bate com o item',
  diverge: 'Diverge',
  inconclusivo: 'Inconclusivo',
  sem_texto: 'Sem texto, validar à mão',
};

/** Por que a leitura deu o veredito que deu. A chave é o código do vocabulário; nenhum texto vem do modelo. */
export const ROTULO_DO_MOTIVO: Record<string, string> = {
  titulo_nao_reconhecido: 'o título não é o deste item',
  campo_obrigatorio_ausente: 'falta um campo que o item exige',
  exigencia_nao_atendida: 'falta o que só uma pessoa valida',
  documento_de_outra_pessoa: 'documento de outra pessoa',
  identidade_nao_encontrada: 'não achei o paciente no documento',
  outro_item: 'é de outro item, ou de um item que já tem o documento certo',
  sem_texto: 'o PDF não tem texto (foto ou escaneado)',
  nao_e_pdf: 'o arquivo não é um PDF',
  agente_indisponivel: 'a leitura automática não respondeu',
  liberacao_pendente: 'a leitura automática ainda não foi liberada',
  pdf_grande_demais: 'o arquivo é grande demais para a leitura',
  fora_da_lista: 'a leitura não bateu com os itens deste caso',
};

export const ROTULO_DA_LEITURA_DO_ARQUIVO: Record<EstadoDaLeitura, string> = {
  pendente: 'aguardando leitura',
  concluida: 'lido',
  falhou: 'a leitura falhou, validar à mão',
};
